import styled from "styled-components";
import type {
  ExclusionsModel,
  RiskScoreModelDetailed,
} from "../../../models/RiskScoreModel";
import Subtitle from "../../atoms/typography/Subtitle";
import {
  getRiskLevel,
  getRiskLevelColor,
  getRiskScoreTitleAndDescription,
} from "../../../utils/amlHelpers";
import BodySmall from "../../atoms/typography/BodySmall";
import Body from "../../atoms/typography/Body";
import { useTranslation } from "react-i18next";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { theme } from "../../../theme";
import Small from "../../atoms/typography/Small";
import Divider from "../../atoms/Divider";
import TextField from "../../atoms/inputs/TextField";
import { Row } from "react-grid-system";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { useState } from "react";
import { useCreateAmlRuleComment } from "../../../graphql/hooks/aml/useCreateAmlRuleComment";
import Alert from "../Alert";
import RiskScoreParameter from "./RiskScoreParameter";
import type { Parameter } from "../../../models/AmlParameter";
import dayjs from "dayjs";
import { useDeleteAmlRuleReferenceExclusion } from "../../../graphql/hooks/aml/useDeleteAmlRuleReferenceExclusion";
import { TrashIcon } from "../../atoms/icons/TrashIcon";

interface Props {
  rule: RiskScoreModelDetailed;
  clientIdentifier: string;
  riskScoreId: string;
  activeExclusions?: Array<ExclusionsModel>;
  refetchRiskScore?: () => void;
}

const ScoreContainer = styled.div`
  width: 70px;
  flex-shrink: 0;
`;

const ParameterContainer = styled.div`
  margin: 8px 0;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey05};
`;

const ClientRiskRuleDetailedActionComponent: React.FC<Props> = ({
  rule,
  clientIdentifier,
  riskScoreId,
  activeExclusions,
  refetchRiskScore,
}) => {
  const { t } = useTranslation();
  const [generalComment, setGeneralComment] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState<
    { type: "comment" | "delete"; value: string } | undefined
  >(undefined);

  const onCompletedCreateComment = (result?: any) => {
    if (result?.aml?.createAmlRuleComment?.success) {
      setSuccessMessage({
        type: "comment",
        value: t("aml.commentAddedSuccessfully"),
      });
      setGeneralComment("");
      setTimeout(() => {
        setSuccessMessage(undefined);
      }, 3000);
    } else {
      setErrorMessage(t("aml.commentCouldNotBeAdded"));
    }
  };
  const onErrorCreateComment = (e?: any) => {
    setErrorMessage(e?.toString());
  };
  const {
    createAmlRuleComment,
    loading: commentLoading,
    error: commentError,
  } = useCreateAmlRuleComment({
    onCompleted: onCompletedCreateComment,
    onError: onErrorCreateComment,
  });

  const onClickSaveGeneralComment = async () => {
    try {
      await createAmlRuleComment({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            ruleId: rule.id,
            comment: generalComment,
          },
        },
      });
    } catch (error) {
      setErrorMessage((error as string).toString());
    }
  };

  const getRelevantExclusions = (
    exclusions: Array<ExclusionsModel>,
    parameters: Array<Parameter>,
  ) => {
    const parameterIds = parameters.map((param) => param.id);
    return exclusions.filter((exclusion) =>
      parameterIds.includes(exclusion.referenceIdentifier),
    );
  };

  const relevantParameterExclusions = getRelevantExclusions(
    activeExclusions ?? [],
    rule.parameters,
  );

  const onCompletedDeleteExclusion = (result?: any) => {
    if (result?.aml?.deleteAmlRuleReferenceExclusion?.success) {
      setSuccessMessage({ type: "delete", value: t("aml.exclusionDeleted") });
      setTimeout(() => {
        setSuccessMessage(undefined);
      }, 5000);
    } else {
      setErrorMessage(t("aml.exclusionCouldNotBeDeleted"));
    }
  };
  const onErrorDeleteExclusion = (e?: any) => {
    setErrorMessage(e?.toString());
  };
  const { deleteAmlRuleReferenceExclusion, error, loading } =
    useDeleteAmlRuleReferenceExclusion({
      onCompleted: onCompletedDeleteExclusion,
      onError: onErrorDeleteExclusion,
    });

  const onDeleteExclusion = async (id: string) => {
    try {
      await deleteAmlRuleReferenceExclusion({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            id: id,
          },
        },
      });
    } catch (error) {
      setErrorMessage((error as string).toString());
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <ScoreContainer>
          <Subtitle
            color={getRiskLevelColor(rule.score, rule.manualRiskScore)}
            textalign="center"
            fontWeight={700}
          >
            {rule.manualRiskScore ?? rule.score}
            {rule?.ruleResult === "MISSING_DATA" && "!"}
          </Subtitle>
          <BodySmall
            fontweight={600}
            color={getRiskLevelColor(rule.score, rule.manualRiskScore)}
            textalign="center"
          >
            {getRiskLevel(rule.score)}
          </BodySmall>
        </ScoreContainer>

        <Body fontweight="500">
          {t(getRiskScoreTitleAndDescription(rule.type).title)}
        </Body>
      </div>

      <HorizontalSpacer spacing={1} />
      {rule?.dominantRule && (
        <div>
          <BodySmall color={theme.colors.textSecondary}>
            {t("aml.dominantRule")}
          </BodySmall>
          <HorizontalSpacer spacing={0.5} />
        </div>
      )}
      <BodySmall color={theme.colors.textSecondary}>
        {rule.description}
      </BodySmall>
      <HorizontalSpacer spacing={0.5} />
      {rule?.ruleResult === "MISSING_DATA" && (
        <div>
          <BodySmall color={theme.colors.textSecondary}>
            {t("aml.ruleIsMissingDataAndGetsAutomaticScore")}
          </BodySmall>
          <HorizontalSpacer spacing={0.5} />
        </div>
      )}
      <BodySmall color={theme.colors.textSecondary}>
        {rule.highlightInfo?.map((i) => i.value)}
      </BodySmall>
      <Small color={theme.colors.textMuted}>{t("aml.valueUpdatesDaily")}</Small>

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />

      <div>
        <TextField
          placeholder={t("common.comment")}
          value={generalComment}
          onChange={(v) => setGeneralComment(v)}
          fullWidth
        />
        <HorizontalSpacer spacing={0.5} />
        <Row nogutter justify="end">
          <OutlinedButton
            title={t("aml.leaveComment")}
            color={theme.colors.primary}
            loading={commentLoading}
            onClick={onClickSaveGeneralComment}
          />
        </Row>
        {(commentError || errorMessage || error) && (
          <Alert
            type="error"
            title={t("errors.somethingWentWrong")}
            message={
              errorMessage || commentError?.toString() || error?.toString()
            }
          />
        )}
        {successMessage?.type === "comment" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <BodySmall color={theme.colors.success}>
              {successMessage.value}
            </BodySmall>
            <OutlinedButton
              title={t("aml.refresh")}
              color={theme.colors.primary}
              onClick={() => {
                refetchRiskScore && refetchRiskScore();
              }}
            />
          </div>
        )}
      </div>

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={1} />

      {rule?.parameters.map((parameter) => (
        <ParameterContainer key={parameter.id}>
          <RiskScoreParameter
            parameter={parameter}
            clientIdentifier={clientIdentifier}
            riskScoreId={riskScoreId}
            ruleType={rule.type}
            parameterExclusionOptions={rule.parameterExclusionOptions}
            hasActiveExclusion={
              relevantParameterExclusions?.find(
                (exclusion) => exclusion.referenceIdentifier === parameter.id,
              ) !== undefined
            }
            refetchRiskScore={refetchRiskScore}
          />
        </ParameterContainer>
      ))}

      <HorizontalSpacer spacing={1} />
      {relevantParameterExclusions?.length > 0 && (
        <Body>{t("aml.currentExceptions")}</Body>
      )}
      <HorizontalSpacer spacing={0.5} />
      {relevantParameterExclusions?.map((exclusion) => (
        <div key={exclusion.id}>
          <div key={exclusion.id}>
            <Small>
              <b>
                {dayjs(exclusion.createdAt ?? new Date()).format("YYYY-MM-DD")}
              </b>{" "}
              - {exclusion.createdByUser?.firstName}{" "}
              {exclusion.createdByUser?.lastName}
            </Small>
            <HorizontalSpacer spacing={0.25} />
            <BodySmall>
              {exclusion.createdByUser?.firstName}{" "}
              {exclusion.createdByUser?.lastName}{" "}
              {t("aml.createdExceptionFor").toLowerCase()}{" "}
              {
                rule.parameters.find(
                  (param) => param.id === exclusion.referenceIdentifier,
                )?.title
              }
              {t("aml.validUntilBroken")}{" "}
              {exclusion.activeUntilFurtherNotice
                ? t("aml.further").toLowerCase()
                : dayjs(exclusion.activeUntil ?? new Date()).format(
                    "YYYY-MM-DD",
                  )}
            </BodySmall>

            <BodySmall>
              <i>"{exclusion.comment?.content}"</i>
            </BodySmall>
            <HorizontalSpacer spacing={0.5} />
            <Row nogutter justify="end">
              <OutlinedButton
                title={t("aml.deleteExclusion")}
                color={theme.colors.error}
                icon={<TrashIcon color={theme.colors.error} />}
                onClick={() => onDeleteExclusion(exclusion.id)}
                loading={loading}
              />
            </Row>
            {successMessage?.type === "delete" && (
              <BodySmall color={theme.colors.success}>
                {successMessage.value}
              </BodySmall>
            )}
            <HorizontalSpacer spacing={1} />
            <Divider />
            <HorizontalSpacer spacing={1} />
          </div>
        </div>
      ))}

      {(rule.comments?.items?.length ?? 0) > 0 && (
        <div>
          <Body>{t("aml.commentsAndHistory")}</Body>
          <div>
            <HorizontalSpacer spacing={0.5} />

            {rule?.comments ? (
              rule.comments.items?.map((comment) => (
                <div key={comment.id}>
                  <Small>
                    <b>
                      {dayjs(comment.createdAt).format("YYYY-MM-DD")} -{" "}
                      {comment.createdByUser?.firstName}{" "}
                      {comment.createdByUser?.lastName}{" "}
                    </b>
                  </Small>
                  <HorizontalSpacer spacing={0.25} />
                  <BodySmall>
                    <i>"{comment.content}"</i>
                  </BodySmall>

                  <HorizontalSpacer spacing={1} />
                </div>
              ))
            ) : (
              <BodySmall>-</BodySmall>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientRiskRuleDetailedActionComponent;
